import React, { useEffect, useState, useCallback } from 'react';
import { Home, Briefcase, Star, MapPin, Plus, Pencil, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const ICON_MAP = { home: Home, work: Briefcase, star: Star, pin: MapPin };
const ICON_OPTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'work', label: 'Work' },
  { id: 'star', label: 'Favorite' },
  { id: 'pin', label: 'Pin' },
];

const emptyForm = { id: null, label: '', address: '', icon: 'pin' };

const SavedPlacesQuickPicks = ({ onSelect, currentValue }) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManage, setShowManage] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchPlaces = useCallback(async () => {
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.get('/users/saved-places');
      setPlaces(data.items || []);
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlaces(); }, [fetchPlaces]);

  const handleAddCurrent = () => {
    if (currentValue && currentValue.trim()) {
      setForm({ id: null, label: '', address: currentValue.trim(), icon: 'pin' });
    } else {
      setForm(emptyForm);
    }
    setShowForm(true);
  };

  const handleEdit = (p) => { setForm({ id: p.id, label: p.label, address: p.address, icon: p.icon || 'pin' }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.label.trim() || !form.address.trim()) {
      toast({ title: 'Label and address are required', variant: 'destructive' });
      return;
    }
    try {
      const api = (await import('../utils/api')).default;
      if (form.id) {
        await api.put(`/users/saved-places/${form.id}`, {
          label: form.label.trim(), address: form.address.trim(), icon: form.icon,
        });
        toast({ title: 'Updated' });
      } else {
        await api.post('/users/saved-places', {
          label: form.label.trim(), address: form.address.trim(), icon: form.icon,
        });
        toast({ title: 'Saved' });
      }
      setShowForm(false);
      fetchPlaces();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      const api = (await import('../utils/api')).default;
      await api.delete(`/users/saved-places/${id}`);
      toast({ title: 'Removed' });
      fetchPlaces();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  if (loading) return null;

  return (
    <div data-testid="saved-places-section">
      <div className="flex items-center gap-2 flex-wrap">
        {places.slice(0, 6).map(p => {
          const Icon = ICON_MAP[p.icon] || MapPin;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.address)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-700 hover:border-pink-300 hover:bg-pink-50 transition"
              data-testid={`saved-place-${p.label}`}
              title={p.address}
            >
              <Icon className="h-3.5 w-3.5 text-pink-600" />
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={handleAddCurrent}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-pink-50 border border-dashed border-pink-300 text-xs font-medium text-pink-700 hover:bg-pink-100 transition"
          data-testid="saved-place-add-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Save place
        </button>
        {places.length > 0 && (
          <button
            type="button"
            onClick={() => setShowManage(true)}
            className="text-xs text-gray-500 hover:text-pink-600 ml-1"
            data-testid="saved-places-manage-btn"
          >
            Manage
          </button>
        )}
      </div>

      {/* Manage dialog */}
      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent className="sm:max-w-md" data-testid="saved-places-manage-dialog">
          <DialogHeader><DialogTitle>Saved Places</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {places.length === 0 && <p className="text-sm text-gray-500">No places saved yet.</p>}
            {places.map(p => {
              const Icon = ICON_MAP[p.icon] || MapPin;
              return (
                <div key={p.id} className="flex items-center gap-2 p-2 border rounded-lg">
                  <Icon className="h-4 w-4 text-pink-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{p.label}</p>
                    <p className="text-xs text-gray-500 truncate">{p.address}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(p.id)} data-testid={`saved-place-delete-${p.label}`}><X className="h-3.5 w-3.5" /></Button>
                </div>
              );
            })}
          </div>
          <Button onClick={() => { setForm(emptyForm); setShowForm(true); setShowManage(false); }} className="w-full bg-pink-600 hover:bg-pink-700">
            <Plus className="h-4 w-4 mr-1" /> Add new place
          </Button>
        </DialogContent>
      </Dialog>

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md" data-testid="saved-place-form-dialog">
          <DialogHeader><DialogTitle>{form.id ? 'Edit place' : 'Save a place'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Home, Work, Mom's..." data-testid="saved-place-label-input" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Kasama Town Centre" data-testid="saved-place-address-input" />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="flex gap-2 mt-1">
                {ICON_OPTIONS.map(o => {
                  const Icon = ICON_MAP[o.id];
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setForm({ ...form, icon: o.id })}
                      className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border ${form.icon === o.id ? 'border-pink-600 bg-pink-50' : 'border-gray-200 bg-white'}`}
                    >
                      <Icon className="h-4 w-4 text-pink-600" />
                      <span className="text-[10px]">{o.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={handleSave} data-testid="saved-place-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedPlacesQuickPicks;
